import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Target, Send, GraduationCap, ArrowRight } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EDUCATION_OPTIONS = [
  '10th Class', '11th/12th (Intermediate)', 'Diploma', 'B.Tech/B.E.', 'B.Sc', 'B.Com', 'BBA/BBM',
  'B.Pharm', 'MBBS/BDS', 'M.Tech/M.E.', 'MBA', 'M.Sc', 'Other'
];
const FUTURE_OPTIONS = [
  'Get a Job', 'Higher Studies in India', 'Study Abroad', 'Start a Business',
  'Prepare for Government Exams', 'Freelancing / Remote Work', 'Not Sure Yet'
];

const LeadFormPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [form, setForm] = useState({
    name: '', phone: '', email: '', city: '', age: '',
    education_level: '', college_name: '', year_of_study: '',
    future_plan: '', dream_career: '', preferred_city: ''
  });

  const update = (field, value) => setForm(prev => ({ ...prev, [field]: value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone || !form.email || !form.education_level || !form.future_plan) {
      toast.error('Please fill all required fields'); return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/leads`, form);
      setSubmitted(true);
    } catch (e) {
      toast.error('Submission failed. Please try again.');
    } finally { setLoading(false); }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-lg border border-earth-200 p-10 max-w-md text-center animate-fade-in-up">
          <div className="w-20 h-20 bg-success/10 rounded-full flex items-center justify-center mx-auto mb-5">
            <Send className="w-10 h-10 text-success" />
          </div>
          <h2 className="text-2xl font-bold text-earth-700 mb-3">Thank You, {form.name}!</h2>
          <p className="text-earth-600 mb-6">We've received your details. Our career counselor will contact you within 24 hours.</p>
          <div className="space-y-3">
            <Button onClick={() => navigate('/faqs')} className="w-full bg-primary hover:bg-primary-hover text-white h-12 rounded-xl btn-press">
              Explore Career Paths <ArrowRight className="ml-2 w-4 h-4" />
            </Button>
            <Button onClick={() => navigate('/')} variant="outline" className="w-full h-12 rounded-xl border-earth-200 text-earth-600">
              Back to Home
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <nav className="glass-nav sticky top-0 z-50 px-4 md:px-8 py-4 border-b border-earth-200">
        <div className="max-w-2xl mx-auto flex items-center gap-2.5 cursor-pointer" onClick={() => navigate('/')}>
          <div className="w-10 h-10 bg-primary rounded-xl flex items-center justify-center">
            <Target className="w-5 h-5 text-white" />
          </div>
          <h1 className="text-2xl font-bold text-earth-700">O4S.ai</h1>
        </div>
      </nav>

      <div className="max-w-2xl mx-auto px-4 py-10">
        <div className="text-center mb-8">
          <div className="w-14 h-14 bg-secondary/10 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <GraduationCap className="w-7 h-7 text-secondary" />
          </div>
          <h2 className="text-3xl font-bold text-earth-700 mb-2">Get Free Career Guidance</h2>
          <p className="text-earth-600">Fill this form and our expert counselor will reach out to you personally</p>
        </div>

        <form onSubmit={handleSubmit} className="bg-white rounded-2xl border border-earth-200 shadow-sm p-6 md:p-8 space-y-6">
          {/* Basic Details */}
          <div>
            <h3 className="text-lg font-bold text-earth-700 mb-4 pb-2 border-b border-earth-100">Basic Details</h3>
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-semibold text-earth-600 mb-1 block">Full Name *</label>
                <Input data-testid="page-lead-name" placeholder="Enter your full name" value={form.name} onChange={e => update('name', e.target.value)} className="border-earth-200 rounded-xl" />
              </div>
              <div>
                <label className="text-sm font-semibold text-earth-600 mb-1 block">Phone *</label>
                <Input data-testid="page-lead-phone" placeholder="10-digit mobile" value={form.phone} onChange={e => update('phone', e.target.value)} className="border-earth-200 rounded-xl" type="tel" />
              </div>
              <div>
                <label className="text-sm font-semibold text-earth-600 mb-1 block">Email *</label>
                <Input data-testid="page-lead-email" placeholder="your@email.com" value={form.email} onChange={e => update('email', e.target.value)} className="border-earth-200 rounded-xl" type="email" />
              </div>
              <div>
                <label className="text-sm font-semibold text-earth-600 mb-1 block">City *</label>
                <Input data-testid="page-lead-city" placeholder="Hyderabad" value={form.city} onChange={e => update('city', e.target.value)} className="border-earth-200 rounded-xl" />
              </div>
            </div>
          </div>

          {/* Education */}
          <div>
            <h3 className="text-lg font-bold text-earth-700 mb-4 pb-2 border-b border-earth-100">Current Education</h3>
            <label className="text-sm font-semibold text-earth-600 mb-2 block">Education Level *</label>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-2 mb-4">
              {EDUCATION_OPTIONS.map(opt => (
                <button key={opt} type="button" onClick={() => update('education_level', opt)}
                  className={`text-left px-3 py-2.5 rounded-xl text-sm border transition-all ${form.education_level === opt ? 'border-primary bg-primary/5 text-primary font-semibold' : 'border-earth-200 text-earth-600 hover:border-earth-300'}`}>
                  {opt}
                </button>
              ))}
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-semibold text-earth-600 mb-1 block">College / School</label>
                <Input placeholder="Institution name" value={form.college_name} onChange={e => update('college_name', e.target.value)} className="border-earth-200 rounded-xl" />
              </div>
              <div>
                <label className="text-sm font-semibold text-earth-600 mb-1 block">Year of Study</label>
                <Input placeholder="e.g., 2nd Year" value={form.year_of_study} onChange={e => update('year_of_study', e.target.value)} className="border-earth-200 rounded-xl" />
              </div>
            </div>
          </div>

          {/* Future Plans */}
          <div>
            <h3 className="text-lg font-bold text-earth-700 mb-4 pb-2 border-b border-earth-100">Future Plans</h3>
            <label className="text-sm font-semibold text-earth-600 mb-2 block">What do you plan to do next? *</label>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mb-4">
              {FUTURE_OPTIONS.map(opt => (
                <button key={opt} type="button" onClick={() => update('future_plan', opt)}
                  className={`text-left px-4 py-3 rounded-xl text-sm border transition-all ${form.future_plan === opt ? 'border-primary bg-primary/5 text-primary font-semibold' : 'border-earth-200 text-earth-600 hover:border-earth-300'}`}>
                  {opt}
                </button>
              ))}
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-semibold text-earth-600 mb-1 block">Dream Career</label>
                <Input placeholder="e.g., Software Developer" value={form.dream_career} onChange={e => update('dream_career', e.target.value)} className="border-earth-200 rounded-xl" />
              </div>
              <div>
                <label className="text-sm font-semibold text-earth-600 mb-1 block">Preferred City</label>
                <Input placeholder="e.g., Hyderabad" value={form.preferred_city} onChange={e => update('preferred_city', e.target.value)} className="border-earth-200 rounded-xl" />
              </div>
            </div>
          </div>

          <Button data-testid="page-lead-submit" type="submit" disabled={loading}
            className="w-full bg-primary hover:bg-primary-hover text-white h-14 text-base font-semibold rounded-xl btn-press">
            {loading ? 'Submitting...' : 'Get Free Career Guidance'}
          </Button>
        </form>
      </div>
    </div>
  );
};

export default LeadFormPage;
